import type { AnalysisResult } from '../lib/types';

export const mockCredit: AnalysisResult = {
  slug: 'sample-credit-report',
  title: 'Charter Communications — Credit Memo',
  date: '2026-03-12T16:00:00Z',
  documentType: 'credit-report',
  verdict: 'BEARISH',
  score: -0.42,
  confidence: 81,
  summary:
    'Deteriorating subscriber dynamics, leverage drift higher, and elevated refinancing risk. Hedged language across the cash flow durability section.',
  briefing: {
    overall:
      'The memo strikes a measured but clearly negative posture. Subscriber attrition is quantified and accelerating; leverage drift is acknowledged with an explicit re-rating watchlist call-out.',
    dimensionBreakdown: [
      { label: 'Tone & Confidence', copy: 'Confident only on liquidity. Cautious throughout cash-flow durability and competitive positioning sections.' },
      { label: 'Forward-Looking Posture', copy: 'Forecast revisions all to the downside; FCF range widened.' },
      { label: 'Hedging & Qualifiers', copy: 'High. Concentrated in subscriber and ARPU sections.' },
      { label: 'Risk Disclosure', copy: 'Explicit refinancing watch flag through 2027 maturity wall.' },
      { label: 'Quantification', copy: '34 quantitative claims, all aligned with negative framing.' },
    ],
    entityLandscape: 'Spectrum Wireline pulls negative; Spectrum Mobile and ad tier pull modestly positive.',
    trajectory: 'Sentiment opens neutral on capital structure, troughs in subscriber/ARPU sections, recovers slightly in cost-takeout commentary.',
    linguisticProfile: 'Hedge density well above credit-memo norm. Modal verbs skew conditional. Negation rate elevated.',
  },
  kpi: { confidence: 81, redFlags: 8, entities: 19, sections: 9 },
  bullSignals: [
    { text: 'Liquidity covered through 2026 maturity wall with $4.2B revolver capacity.', paragraphRef: 'P12' },
    { text: 'Spectrum Mobile +18% YoY subscribers with margin profile improving.', paragraphRef: 'P21' },
    { text: 'Cost takeout program tracking ahead of plan with $620M run-rate.', paragraphRef: 'P28' },
  ],
  bearSignals: [
    { text: 'Wireline subscriber attrition accelerated to -2.1% YoY; ARPU compression.', paragraphRef: 'P18' },
    { text: 'Leverage drift to 4.7x with management hedging on the deleveraging trajectory.', paragraphRef: 'P31' },
    { text: '2027 maturity wall flagged on credit watchlist; refinancing market dependent.', paragraphRef: 'P34' },
  ],
  dimensions: [
    { label: 'Tone & Confidence', value: 0.32, qualifier: 'MODERATE' },
    { label: 'Forward-Looking Posture', value: 0.24, qualifier: 'LOW' },
    { label: 'Hedging & Qualifiers', value: 0.78, qualifier: 'HIGH' },
    { label: 'Risk Disclosure', value: 0.86, qualifier: 'HIGH' },
    { label: 'Quantification', value: 0.62, qualifier: 'ELEVATED' },
  ],
  radar: [
    { label: 'Tone', value: 0.32 },
    { label: 'Forward-Looking', value: 0.24 },
    { label: 'Directness', value: 0.22 },
    { label: 'Risk Disclosure', value: 0.86 },
    { label: 'Quantification', value: 0.62 },
  ],
  entities: [
    { name: 'Spectrum Wireline', type: 'Segment', verdict: 'BEARISH', score: -0.62, context: 'Subscriber attrition accelerating; ARPU compression.' },
    { name: 'Spectrum Mobile', type: 'Segment', verdict: 'BULLISH', score: 0.41, context: '+18% YoY subscribers; margin improving.' },
    { name: '2027 Notes', type: 'Product', verdict: 'BEARISH', score: -0.55, context: 'Refinancing watch through 2027 maturity wall.' },
    { name: 'Charter Mgmt', type: 'Person', verdict: 'MIXED', score: 0.0, context: 'Hedged but transparent on deleveraging.' },
    { name: 'FTTH Competition', type: 'Geography', verdict: 'BEARISH', score: -0.38, context: 'Overbuild pressure in 14% of footprint.' },
  ],
  sections: [
    { idx: 1, title: 'Capital Structure', tags: ['capital'], summary: 'Liquidity adequate; leverage drifting.', range: 'P1–P10', verdict: 'MIXED', score: -0.05 },
    { idx: 2, title: 'Subscriber Dynamics', tags: ['kpi'], summary: 'Wireline accelerating; mobile offset partial.', range: 'P11–P22', verdict: 'BEARISH', score: -0.51 },
    { idx: 3, title: 'ARPU & Mix', tags: ['kpi'], summary: 'Compression in wireline; mobile mix favourable.', range: 'P23–P27', verdict: 'BEARISH', score: -0.38 },
    { idx: 4, title: 'Cost Takeout', tags: ['cost'], summary: 'Tracking ahead of plan.', range: 'P28–P30', verdict: 'BULLISH', score: 0.42 },
    { idx: 5, title: 'Leverage & Maturity', tags: ['risk'], summary: '2027 wall on watchlist.', range: 'P31–P34', verdict: 'BEARISH', score: -0.62 },
    { idx: 6, title: 'Competitive Positioning', tags: ['competition'], summary: 'FTTH overbuild flagged.', range: 'P35–P38', verdict: 'BEARISH', score: -0.41 },
  ],
  qna: [],
  numerical: [
    { metric: 'Net Leverage', value: '4.7x', period: '2026YE', direction: 'up', framing: 'Drift higher; deleveraging path hedged.', aligned: true },
    { metric: 'Wireline Subs', value: '-2.1% YoY', period: 'Q4 2026', direction: 'down', framing: 'Acceleration vs prior quarter.', aligned: true },
    { metric: 'Spectrum Mobile Subs', value: '+18% YoY', period: 'Q4 2026', direction: 'up', framing: 'Continued strength; margin improving.', aligned: true },
    { metric: 'FCF', value: '$3.8B', period: '2026E', direction: 'down', framing: 'Range widened to the downside.', aligned: true },
    { metric: 'Liquidity', value: '$4.2B', period: '2026YE', direction: 'star', framing: 'Adequate through 2026 maturity wall.', aligned: true },
  ],
  linguisticIndicators: [
    { label: 'Hedge Density', value: '0.41', rawValue: 0.82, tooltip: 'Well above credit-memo norm.' },
    { label: 'Forward Modals', value: '0.18', rawValue: 0.36 },
    { label: 'Active Voice', value: '0.71', rawValue: 0.71 },
    { label: 'Sentence Length', value: '23 words', rawValue: 0.58 },
    { label: 'Lexical Density', value: '0.69', rawValue: 0.69 },
    { label: 'Modal Confidence', value: '0.32', rawValue: 0.32 },
    { label: 'Quantification', value: '34 claims', rawValue: 0.62 },
    { label: 'Negation', value: '0.21', rawValue: 0.42 },
  ],
  fingerprint: [
    { label: 'Confidence Words', value: 0.32 },
    { label: 'Forward-Looking Verbs', value: 0.24 },
    { label: 'Quantified Claims', value: 0.62 },
    { label: 'Active Voice', value: 0.71 },
    { label: 'Concrete Nouns', value: 0.78 },
    { label: 'Hedges (inv.)', value: 0.18 },
    { label: 'Filler Phrases (inv.)', value: 0.62 },
    { label: 'Negation (inv.)', value: 0.58 },
  ],
  sectorLexicon: [
    { name: 'Telecom Wireline', matches: 41, pos: 8, neg: 24, risk: 9, score: -0.42 },
    { name: 'Telecom Mobile', matches: 28, pos: 19, neg: 4, risk: 5, score: 0.48 },
    { name: 'Capital Structure', matches: 32, pos: 11, neg: 14, risk: 7, score: -0.18 },
    { name: 'Cost & Operating Leverage', matches: 18, pos: 12, neg: 3, risk: 3, score: 0.41 },
  ],
  riskQuotes: [
    { quote: 'Refinancing of the 2027 notes will depend on credit market conditions at that time.', ref: 'P34', category: 'macro' },
    { quote: 'Subscriber attrition has accelerated in over-built markets.', ref: 'P18', category: 'competitive' },
    { quote: 'Cost-of-debt sensitivity remains elevated.', ref: 'P32', category: 'cost' },
  ],
  verification: [
    { label: 'Numerical claims aligned with framing', status: 'pass', note: '34 of 34 aligned.' },
    { label: 'Forward-looking statements properly flagged', status: 'pass' },
    { label: 'Risk Factor Coverage', status: 'pass' },
    { label: 'Sector lexicon coverage', status: 'pass' },
    { label: 'Source paragraphs cite-aligned', status: 'pass' },
  ],
  advanced: [
    { key: 'Model Version', value: 'tia-sentiment-v3.4' },
    { key: 'Total Tokens', value: '14,288' },
    { key: 'Sections Detected', value: '9' },
    { key: 'Entities Detected', value: '19' },
    { key: 'Latency', value: '3.4s' },
    { key: 'Run ID', value: 'run_8wdf2qx7lp' },
  ],
};
